import * as React from "react";
import {Header} from "@/app/header/Header";
import {Footer} from "@/app/footer/Footer";
import {useAppTheme as useThemeContext} from "@/app/AppThemeContext";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import {AppBar, Container, Grid2, Typography, useTheme} from "@mui/material";
import {useLocalState} from "@/lib/localstate";
import {Layout, LayoutItem} from "@/lib/components/Layout";
import {UnsavedChangesDialog} from "@/lib/components/UnsavedChangesDialog";
import {useUnsavedChangesBlocker} from "@/lib/unsaved-changes-blocker";
import {ModelState} from "@/lib/modelstate";
import {StatusFooter} from "@/app/footer/StatusFooter";
import {NotFound} from "@/lib/components/NotFound";

export type AppFrameProps = {
    loading?: boolean;
    modelState?: ModelState;
    httpErrorStatus?: number;
    padding?: number;
    buttonsGrow?: boolean;
    title?: React.ReactNode;
    isDirty?: boolean;
    buttons?: React.ReactNode;
    buttonsLeft?: React.ReactNode;
    children: React.ReactNode;
};

export const AppFrame = ({
    loading,
    modelState,
    httpErrorStatus,
    title,
    buttons,
    buttonsLeft,
    buttonsGrow,
    padding,
    isDirty,
    children
}: AppFrameProps) => {
    const { mode, toggleColorMode } = useThemeContext();
    const theme = useTheme();
    const [expanded, setExpanded] = useLocalState("frame-expanded", false);
    const maxWidth: any = expanded ? "100%" : "xl";
    const isNotFound = modelState === "error" && httpErrorStatus === 404;
    const toolbar = (title || buttons) && !isNotFound;

    const blocker = useUnsavedChangesBlocker(isDirty);

    const busy = loading || modelState === "loading" || modelState === "saving";

    const blur = busy
        ? {
              "& > div > *": {
                  filter: "blur(2px)"
              }
          }
        : undefined;

    return (
        <Container disableGutters maxWidth={maxWidth} sx={{ position: "relative" }}>
            {busy && <PageLoadingSpinner />}
            <Layout root sx={{ ...blur, marginLeft: 1, marginRight: 1 }}>
                <LayoutItem>
                    <Header
                        mode={mode}
                        toggleColorMode={toggleColorMode}
                        expanded={expanded}
                        setExpanded={setExpanded}
                    />
                </LayoutItem>
                {toolbar && (
                    <LayoutItem>
                        <AppBar
                            position="relative"
                            variant="outlined"
                            sx={{
                                backgroundColor: "transparent",
                                // backgroundColor: mode === "dark" ? "black" : "white",
                                color: theme.palette.text.primary,
                                // borderBottomLeftRadius: expanded ? undefined : 4,
                                // borderBottomRightRadius: expanded ? undefined : 4,
                                // zIndex: theme.zIndex.drawer,
                                linearGradient: "none",
                                paperShadow: "none",
                                border: "none"
                                // top: 64,
                            }}
                        >
                            <Grid2 container direction="column">
                                <Grid2 container direction="row" paddingTop={2}>
                                    <Grid2 flexGrow={1}>{buttonsLeft}</Grid2>
                                    <Grid2 flexGrow={buttonsGrow ? 1 : 0}>{buttons}</Grid2>
                                </Grid2>
                                {title && (
                                        <Grid2 container justifyContent="center" paddingY={6}>
                                            <Grid2>
                                                <Typography variant="h4">{title}</Typography>
                                            </Grid2>
                                        </Grid2>
                                )}
                            </Grid2>
                        </AppBar>
                    </LayoutItem>
                )}
                <LayoutItem body>
                    <Layout>
                        <LayoutItem grow padding={padding}>
                            {isNotFound ? <NotFound /> : children}
                        </LayoutItem>
                        <LayoutItem>
                            <Footer />
                        </LayoutItem>
                    </Layout>
                </LayoutItem>
                <LayoutItem>
                    <StatusFooter />
                </LayoutItem>
            </Layout>
            <UnsavedChangesDialog
                open={blocker.blocked} // Prevents second popup
                onConfirm={blocker.proceed}
                onCancel={blocker.reset}
            />
        </Container>
    );
};
